import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { BinaryBlob, BinaryBlobResponse, BundleManifestArtifact, BundleManifestComponent, Release } from "../../models";
import { HTTP_CLIENT } from "../../hooks";
import {
  BundleManifestComponentType,
  BundleManifestComponentPackage,
  BundleManifestArtifactId,
  DATA_TYPE_APPLICATION_PROD,
  DATA_TYPE_BOOTLOADER_PROD,
  DATA_TYPE_SE_FIDO_LOGIC_PROD,
  DATA_TYPE_SE_FIDO_PACKAGE_PROD,
  DATA_TYPE_SE_PIV_LOGIC_PROD,
  DATA_TYPE_SE_PIV_PACKAGE_PROD,
  SEMVER_PATTERN_FULL,
  SEMVER_PATTERN_REDUCED,
} from "../../utils";
import { RequiredMark } from "../FormFields";
import { LabeledSelect } from "./LabeledSelect";
import { ArtifactRow } from "./ArtifactRow";

interface Props {
  show: boolean;
  close: () => void;
  onSave: (updatedComponent: BundleManifestComponent) => void;
  existingComponent?: BundleManifestComponent;
  bootloaderReleases: Release[];
  applicationReleases: Release[];
  fidoReleases: Release[];
  pivReleases: Release[];
}

const SE_ARTIFACT_IDS: BundleManifestArtifactId[] = [
  BundleManifestArtifactId.LOGIC,
  BundleManifestArtifactId.INTERFACE,
  BundleManifestArtifactId.CREDENTIAL_MANAGER,
];

export function ComponentEditorModal({
  show,
  close,
  onSave,
  existingComponent,
  bootloaderReleases,
  applicationReleases,
  fidoReleases,
  pivReleases,
}: Props) {
  const [componentName, setComponentName] = useState<string | undefined>(undefined);
  const [componentType, setComponentType] = useState<BundleManifestComponentType | undefined>(undefined);
  const [componentPackage, setComponentPackage] = useState<BundleManifestComponentPackage | undefined>(undefined);
  const [componentRelease, setComponentRelease] = useState<Release | undefined>(undefined);
  const [componentArtifacts, setComponentArtifacts] = useState<BundleManifestArtifact[]>([]);
  const [packageMd5, setPackageMd5] = useState<string | undefined>(undefined);

  const releasesForComponentType: Release[] =
    componentType === BundleManifestComponentType.BOOTLOADER
      ? bootloaderReleases
      : componentType === BundleManifestComponentType.APPLICATION
        ? applicationReleases
        : componentType === BundleManifestComponentType.SE && componentPackage === BundleManifestComponentPackage.FIDO
          ? fidoReleases
          : componentType === BundleManifestComponentType.SE && componentPackage === BundleManifestComponentPackage.PIV
            ? pivReleases
            : [];
  
  const fetchReleaseBlobs = async (releaseId: number): Promise<BinaryBlob[]> => {
    const response = await HTTP_CLIENT.get<BinaryBlobResponse>({
      path: "/api/v2/binaryBlobs/search/findAnonymouslyByRelease",
      query: { release: releaseId },
    });
    return response._embedded.binaryBlobs;
  };

  const getArtifact = (id: BundleManifestArtifactId) =>
    componentArtifacts.find((artifact) => artifact.id === id);

  const logicMd5 = getArtifact(BundleManifestArtifactId.LOGIC)?.md5;
  const hasLogicMd5 = !!logicMd5;

  const isVersionValid =
    componentType === BundleManifestComponentType.SE
      ? (hasLogicMd5 ? SE_ARTIFACT_IDS : [BundleManifestArtifactId.LOGIC]).every((id) =>
          SEMVER_PATTERN_REDUCED.test(getArtifact(id)?.version ?? ""),
        )
      : SEMVER_PATTERN_FULL.test(getArtifact(BundleManifestArtifactId.BINARY)?.version ?? "");

  const canSave =
    componentType && componentName && componentName.trim() !== "" &&
    componentArtifacts.length > 0 &&
    isVersionValid &&
    (componentType !== BundleManifestComponentType.SE || componentPackage !== undefined);

  const updateExistingArtifactMd5 = (id: BundleManifestArtifactId, md5: string | undefined) =>
    setComponentArtifacts((artifacts) => artifacts.map((a) => a.id === id ? { ...a, md5 } : a));

  const updateExistingArtifactVersion = (id: BundleManifestArtifactId, version: string) =>
    setComponentArtifacts((artifacts) => artifacts.map((a) => a.id === id ? { ...a, version } : a));

  const findReleaseByArtifact = async (
    targetComponent: BundleManifestComponent,
    targetComponentType: BundleManifestComponentType,
    targetComponentPackage: BundleManifestComponentPackage | undefined,
  ): Promise<Release | undefined> => {
    const config = ((): { releases: Release[]; dataType: number; lookupMd5: string | undefined } | undefined => {
      switch (targetComponentType) {
        case BundleManifestComponentType.BOOTLOADER:
        case BundleManifestComponentType.APPLICATION:
          return {
            releases: targetComponentType === BundleManifestComponentType.BOOTLOADER ? bootloaderReleases : applicationReleases,
            dataType: targetComponentType === BundleManifestComponentType.BOOTLOADER ? DATA_TYPE_BOOTLOADER_PROD : DATA_TYPE_APPLICATION_PROD,
            lookupMd5: targetComponent.artifacts.find((a) => a.id === BundleManifestArtifactId.BINARY)?.md5,
          };
        case BundleManifestComponentType.SE:
          if (!targetComponentPackage) return undefined;
          return {
            releases: targetComponentPackage === BundleManifestComponentPackage.FIDO ? fidoReleases : pivReleases,
            dataType: targetComponentPackage === BundleManifestComponentPackage.FIDO
              ? DATA_TYPE_SE_FIDO_PACKAGE_PROD
              : DATA_TYPE_SE_PIV_PACKAGE_PROD,
            lookupMd5: targetComponent.package_md5,
          };
        default:
          return undefined;
      }
    })();

    if (config && config.lookupMd5) {
      for (const release of config.releases) {
        const blobs = await fetchReleaseBlobs(release.id);
        if (blobs.find((blob) => blob.dataTypeRaw === config.dataType)?.hash === config.lookupMd5) return release;
      }
    }

    return undefined;
  };

  const handleComponentReset = (resetComponentType: string | undefined, resetSePackage: string | undefined) => {
    let newComponentPackage = undefined;
    let newComponentName = undefined;
    if (resetComponentType) {
      let newComponentType = undefined;
      switch (resetComponentType) {
        case BundleManifestComponentType.BOOTLOADER:
          newComponentName = "Bootloader";
          newComponentType = BundleManifestComponentType.BOOTLOADER;
          break;
        case BundleManifestComponentType.APPLICATION:
          newComponentName = "Application";
          newComponentType = BundleManifestComponentType.APPLICATION;
          break;
        case BundleManifestComponentType.SE:
          newComponentType = BundleManifestComponentType.SE;
          break;
      }
      setComponentType(newComponentType);
    } else if (resetSePackage) {
      switch (resetSePackage) {
        case BundleManifestComponentPackage.FIDO:
          newComponentName = "FIDO Package";
          newComponentPackage = BundleManifestComponentPackage.FIDO;
          break;
        case BundleManifestComponentPackage.PIV:
          newComponentName = "PIV Package";
          newComponentPackage = BundleManifestComponentPackage.PIV;
          break;
      }
    }

    setComponentArtifacts([]);
    setComponentRelease(undefined);
    setPackageMd5(undefined);
    setComponentName(newComponentName);
    setComponentPackage(newComponentPackage);
  };

  const handleReleaseChange = (releaseId: string | undefined) => {
    const newRelease = releasesForComponentType.find((release) => String(release.id) === releaseId);
    if (!newRelease || !componentType) return;
    if (componentType === BundleManifestComponentType.SE) {
      const logicVersion = SEMVER_PATTERN_REDUCED.test(newRelease.version) ? newRelease.version : "";
      setComponentArtifacts([
        { id: BundleManifestArtifactId.LOGIC, version: logicVersion },
        { id: BundleManifestArtifactId.INTERFACE, version: "" },
        { id: BundleManifestArtifactId.CREDENTIAL_MANAGER, version: "" },
      ]);
    } else {
      const binaryVersion = SEMVER_PATTERN_FULL.test(newRelease.version) ? newRelease.version : "";
      setComponentArtifacts([{ id: BundleManifestArtifactId.BINARY, version: binaryVersion }]);
    }
    setComponentRelease(newRelease);
  };

  const handleSave = () => {
    if (!componentType || !componentName || componentArtifacts.length === 0 || !isVersionValid) return;
    // SE releases that predate the logic artifact have no logic MD5; in that case
    // forward only the logic artifact, which resolves to the package MD5.
    const artifacts =
      componentType === BundleManifestComponentType.SE && !hasLogicMd5
        ? componentArtifacts.filter((artifact) => artifact.id === BundleManifestArtifactId.LOGIC)
        : componentArtifacts;
    onSave({
      type: componentType,
      name: componentName,
      ...(componentType === BundleManifestComponentType.SE ? { package: componentPackage } : {}),
      ...(componentType === BundleManifestComponentType.SE && packageMd5 ? { package_md5: packageMd5 } : {}),
      artifacts,
    });
  };

  useEffect(() => {
    if (!show) return;

    const existingComponentType = existingComponent?.type;
    const existingComponentPackage = existingComponent?.package;
    setComponentType(existingComponentType);
    setComponentName(existingComponent?.name);
    setComponentPackage(existingComponentPackage);
    setComponentArtifacts(existingComponent?.artifacts ?? []);
    setComponentRelease(undefined);
    setPackageMd5(existingComponent?.package_md5);

    if (!existingComponent || !existingComponentType) return;
    void (async () => {
      try {
        const existingComponentRelease = await findReleaseByArtifact(existingComponent, existingComponentType, existingComponentPackage);
        if (!existingComponentRelease) return;
        setComponentRelease(existingComponentRelease);
      } catch (e: any) {
        console.error(e.stack);
        toast.error(e.message);
      }
    })();
  }, [show, existingComponent]);

  // Bootloader/Application releases provide a single binary blob whereas SE
  // releases provide both a Package and Logic JWT blob.
  useEffect(() => {
    if (!componentRelease || !componentType || (componentType === BundleManifestComponentType.SE && !componentPackage)) return;

    void (async () => {
      try {
        const blobs = await fetchReleaseBlobs(componentRelease.id);
        if (blobs.length === 0) return;

        switch (componentType) {
          case BundleManifestComponentType.BOOTLOADER:
          case BundleManifestComponentType.APPLICATION:
          {
            const binaryDataType = componentType === BundleManifestComponentType.BOOTLOADER ? DATA_TYPE_BOOTLOADER_PROD : DATA_TYPE_APPLICATION_PROD;
            updateExistingArtifactMd5(BundleManifestArtifactId.BINARY, blobs.find((blob) => blob.dataTypeRaw === binaryDataType)?.hash);
            break;
          }
          case BundleManifestComponentType.SE:
            switch (componentPackage) {
              case BundleManifestComponentPackage.FIDO:
              case BundleManifestComponentPackage.PIV:
              {
                const logicDataType = componentPackage === BundleManifestComponentPackage.FIDO ? DATA_TYPE_SE_FIDO_LOGIC_PROD : DATA_TYPE_SE_PIV_LOGIC_PROD;
                const packageDataType = componentPackage === BundleManifestComponentPackage.FIDO ? DATA_TYPE_SE_FIDO_PACKAGE_PROD : DATA_TYPE_SE_PIV_PACKAGE_PROD;
                updateExistingArtifactMd5(BundleManifestArtifactId.LOGIC, blobs.find((blob) => blob.dataTypeRaw === logicDataType)?.hash);
                setPackageMd5(blobs.find((blob) => blob.dataTypeRaw === packageDataType)?.hash);
                break;
              }
            }
            break;
        }
      } catch (e: any) {
        console.error(e.stack);
        toast.error(e.message);
      }
    })();
  }, [componentType, componentPackage, componentRelease?.id]);

  return (
    <Modal show={show} onHide={close} size="lg" contentClassName="bg-light">
      <Modal.Header closeButton closeVariant="white" className="bg-secondary text-white">
        {existingComponent ? "Edit Component" : "Add Component"}
      </Modal.Header>
      <Modal.Body className="text-dark">
        <Form>
          <LabeledSelect
            controlId="componentType"
            label="Type"
            placeholder="-- select type --"
            value={componentType}
            options={Object.values(BundleManifestComponentType).map((t) => ({ value: t, label: t }))}
            onChange={(newType) => handleComponentReset(newType, undefined)}
          />

          {componentType === BundleManifestComponentType.SE && (
            <LabeledSelect
              controlId="componentPackage"
              label="Package"
              placeholder="-- select package --"
              value={componentPackage}
              options={Object.values(BundleManifestComponentPackage).map((p) => ({ value: p, label: p }))}
              onChange={(newPackage) => handleComponentReset(undefined, newPackage)}
              className="mb-3 ps-4"
            />
          )}

          {componentType && (componentType !== BundleManifestComponentType.SE || componentPackage) && (
            <>
              <Form.Group controlId="componentName" className="mb-3">
                <Form.Label>
                  Name <RequiredMark />
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={componentName}
                  onChange={(e) => setComponentName(e.target.value)}
                />
              </Form.Group>

              <LabeledSelect
                controlId="componentRelease"
                label="Release"
                placeholder="-- select release --"
                value={componentRelease?.id}
                options={releasesForComponentType.map((r) => ({ value: r.id, label: r.version }))}
                onChange={handleReleaseChange}
              />
            </>
          )}

          {(componentType === BundleManifestComponentType.BOOTLOADER || componentType === BundleManifestComponentType.APPLICATION) && componentRelease && (
            <ArtifactRow
              versionControlId="componentVersion"
              versionLabel="Binary Version"
              versionPlaceholder="e.g. 1.2.3"
              versionPattern={SEMVER_PATTERN_FULL}
              versionInvalidMessage="Must be a semantic version (e.g. 1.2.3)."
              version={getArtifact(BundleManifestArtifactId.BINARY)?.version}
              onVersionChange={(v) => updateExistingArtifactVersion(BundleManifestArtifactId.BINARY, v)}
              md5ControlId="componentBinaryMd5"
              md5Label="Binary MD5"
              md5Value={getArtifact(BundleManifestArtifactId.BINARY)?.md5}
            />
          )}

          {componentType === BundleManifestComponentType.SE && componentRelease && (
            <>
              {(hasLogicMd5
                ? [
                    { id: BundleManifestArtifactId.LOGIC, label: "Logic", md5Label: "Logic MD5", md5Value: logicMd5 },
                    { id: BundleManifestArtifactId.INTERFACE, label: "Interface", md5Label: "Package MD5", md5Value: packageMd5 },
                    { id: BundleManifestArtifactId.CREDENTIAL_MANAGER, label: "Credential Manager", md5Label: undefined, md5Value: undefined },
                  ]
                : [
                    { id: BundleManifestArtifactId.LOGIC, label: "Logic", md5Label: "Package MD5", md5Value: packageMd5 },
                  ]
              ).map(({ id, label, md5Label, md5Value }) => (
                <ArtifactRow
                  key={`se-artifact-${id}`}
                  versionControlId={`componentVersion-${id}`}
                  versionLabel={`${label} Version`}
                  versionPlaceholder="e.g. 1.2"
                  versionPattern={SEMVER_PATTERN_REDUCED}
                  versionInvalidMessage="Must be a major.minor version (e.g. 1.2)."
                  version={getArtifact(id)?.version}
                  onVersionChange={(v) => updateExistingArtifactVersion(id, v)}
                  md5ControlId={md5Label ? `componentMd5-${id}` : undefined}
                  md5Label={md5Label}
                  md5Value={md5Value}
                />
              ))}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="text-dark">
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSave} onClick={handleSave}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
